import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  presentationButtonOverlayVariants,
  presentationButtonVariants,
  presentationScoreCalculationTooltipVariants,
} from '../animation';
import { Scorecard, displayScorecardElementName, getIsBeachMode, ScorecardElementSymbol } from '../../../../Scorecard';
import {
  ScoreCalculationTooltipBody,
  type CalculationScorecardValues,
  scoreCalculationTooltipCalculationScorecardValuesFromScorecard,
  StandaloneScorecardElement,
  StandaloneScorecardElementProps,
} from '../../../components';
import { usePresentationViewOptionsContext } from '../context';
import { Link } from '@remix-run/react';
import { usePresentationInsights } from './PresentationInsights';

interface PresentationScorecardButtonProps extends StandaloneScorecardElementProps {
  hoveredElement: ScorecardElementSymbol | undefined;
  setHoveredElement: Dispatch<SetStateAction<ScorecardElementSymbol | undefined>>;
  to?: string;
  insightLayout?: boolean;
  scorecard?: Scorecard;
  onClick?: () => void;
  elementOverride?: JSX.Element;
  btIsNull?: boolean;
  estateStatusChecklistItems?: CalculationScorecardValues['estateStatusChecklistItems'];
}

export const PresentationScorecardButton: FC<PresentationScorecardButtonProps> = ({
  to,
  insightLayout = false,
  value,
  element,
  className,
  scorecard,
  hoveredElement,
  setHoveredElement,
  onClick = () => {},
  elementOverride,
  estateStatusChecklistItems,
}) => {
  const { t: tPages } = useTranslation('pages');
  const insights = usePresentationInsights();
  const { t: tScorecard } = useTranslation('scorecard');
  const { viewOptions } = usePresentationViewOptionsContext();

  const invisible = !viewOptions?.focusedElements?.includes(element);
  const elementName = displayScorecardElementName(element, tScorecard).replace(/\s+/g, ' ');
  const hovered = element === hoveredElement;
  const animation = hovered ? 'hovered' : hoveredElement === undefined ? 'idle' : 'receded';
  const beachMode = getIsBeachMode(scorecard);
  const beachModeElement =
    beachMode && (element === 'Bt' || element === 'Lt' || element === 'Qt' || element === 'Rt' || element === 'Tt');
  const insight =
    insightLayout && !invisible && !beachModeElement && typeof value === 'number' && Number.isFinite(value)
      ? insights?.insights.find((item) => item.element === element)
      : undefined;
  const selected = !!insight && insights?.selected?.element === element;
  const handleClick = () => {
    setHoveredElement(undefined);
    onClick?.();
  };

  return (
    <>
      <motion.div
        initial="idle"
        animate={animation}
        variants={presentationButtonVariants}
        onHoverStart={() => setHoveredElement(element)}
        onHoverEnd={() => setHoveredElement(undefined)}
        className={clsx(
          className,
          'relative z-[0]',
          insightLayout && 'h-[216px] w-[216px]',
          selected && 'rounded-[20px] outline outline-4 outline-white outline-offset-4',
          to ? 'cursor-pointer' : 'cursor-default'
        )}
        onClick={to ? undefined : handleClick}
      >
        {to ? (
          <Link
            aria-label={tScorecard('open-element-details-label', { element: elementName })}
            className="absolute inset-0 z-20 rounded-[20px] focus-visible:outline focus-visible:outline-4 focus-visible:outline-white"
            style={insight ? { bottom: 56 } : undefined}
            onClick={handleClick}
            to={to}
          />
        ) : null}
        <motion.div
          initial="idle"
          animate={animation}
          variants={presentationButtonOverlayVariants}
          className="pointer-events-none absolute inset-0 left-[-1px] bottom-[1px] z-10 h-[101%] w-[101%] rounded-[20px] bg-darkMode-almostBlack/50"
        />
        <AnimatePresence>
          {hovered && (
            <motion.div
              exit="idle"
              initial="idle"
              animate={animation}
              variants={presentationScoreCalculationTooltipVariants}
              className={`absolute ${
                beachModeElement
                  ? 'top-[calc(100%_-_144px)] origin-center w-fit'
                  : 'top-[calc(100%_+_8px)] left-0 origin-top-left'
              } pointer-events-none`}
            >
              <ScoreCalculationTooltipBody
                isFullscreen={true}
                viewOptions={viewOptions}
                beachMode={beachMode}
                score={value}
                element={element}
                className="dark:!bg-darkMode-gray-floatingMenu"
                calculationScorecardValues={{
                  ...scoreCalculationTooltipCalculationScorecardValuesFromScorecard(scorecard),
                  estateStatusChecklistItems,
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
        {elementOverride ?? (
          <StandaloneScorecardElement
            value={value}
            element={element}
            beachMode={beachMode}
            className={clsx(
              invisible && 'grayscale opacity-40 !bg-darkMode-gray-medium',
              '!h-[216px] !w-[216px] !rounded-[20px] !px-[21.35px] !shadow-none',
              '[&_.StandaloneScorecardElement-element]:!text-[50.233px] [&_.StandaloneScorecardElement-element]:!leading-[53.046px]',
              '[&_.StandaloneScorecardElement-element-name]:!text-[22.539px] [&_.StandaloneScorecardElement-element-name]:!leading-[22.102px] [&_.StandaloneScorecardElement-element-name]:!tracking-[-0.232px]',
              '[&_.StandaloneScorecardElement-score-container]:!h-[124px]',
              '[&_.StandaloneScorecardElement-score]:!text-[70.326px] [&_.StandaloneScorecardElement-score]:!tracking-[-1.256px]',
              '[&_.StandaloneScorecardElement-score-percentage]:!text-[70.326px] [&_.StandaloneScorecardElement-score-percentage]:!tracking-[-1.256px]'
            )}
          />
        )}
        {insight && (
          <button
            type="button"
            aria-label={tPages('full-screen-scorecard-open-insight', { element: elementName })}
            aria-expanded={selected}
            aria-controls={selected ? 'presentation-insight' : undefined}
            data-presentation-control
            className="absolute bottom-3 left-3 right-3 z-30 flex h-10 items-center justify-center gap-3 rounded-lg bg-black/60 text-[20px] font-bold leading-none text-white hover:bg-black/75 focus-visible:outline focus-visible:outline-4 focus-visible:outline-white"
            onMouseEnter={() => setHoveredElement(undefined)}
            onFocus={() => setHoveredElement(undefined)}
            onClick={(event) => {
              event.stopPropagation();
              setHoveredElement(undefined);
              insights?.open(element, event.currentTarget);
            }}
          >
            <span>{tPages('full-screen-scorecard-insight')}</span>
            <span aria-hidden>›</span>
          </button>
        )}
      </motion.div>
    </>
  );
};
